import React, { useRef } from "react";
import * as Styles from "./styles/CompareHeader";
import * as CompareItemStyles from "./styles/CompareItem";
import { getCarNameObject } from "../../utils/formatters/nameFormatters";
import { type CompareV2DispatchType, useCompareV2Selector } from "../../redux/store";
import * as Button from "../../../../common-deprecated/styles/v2/toyota/components/Button";
import Icon from "../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import SelectGrade from "./select-grade/SelectGrade";
import useNewCarHeader from "../../hooks/useNewCarHeader";
import { CompareV2Label, useCompareV2Label } from "../../utils/labels";
import { ToggleIconWrapper } from "../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import useMaxNumberToCompare from "../../../../shared-logic/features/compare/hooks/useMaxNumberToCompare";
import CompareTable from "../common/CompareTable";
import { showModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_DELETE_CAR } from "../../../../shared-logic/features/compare/utils/modalConstants";
import { CompareV2Mode } from "../../../../shared-logic/features/compare/utils/types";
import { SrOnly } from "../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import { useDispatch } from "../../../../shared-logic/redux/store";
import { isRTL } from "../../../../common-deprecated/settings/utils/commonSettingUtils";

type NewCompareHeaderPropsType = {
    expanded: boolean;
    toggleExpanded: () => void;
    onShowSelectNewGrade: (index: number) => void;
    scrollbarWidth: number;
};

const NewCarHeader = (props: NewCompareHeaderPropsType): JSX.Element => {
    const { expanded, toggleExpanded, onShowSelectNewGrade, scrollbarWidth } = props;

    const dispatch = useDispatch<CompareV2DispatchType>();
    const cars = useCompareV2Selector((state) => state.newCarCompare.grades);
    const settings = useCompareV2Selector((state) => state.commonSettings);
    const compareV2Settings = useCompareV2Selector((state) => state.compareV2Settings);
    const isLeasing = useCompareV2Selector(
        (state) => state.newCarCompare.ncCompareContext === NcCompareContext.Leasing,
    );

    const { compareV2Standalone } = compareV2Settings;

    const { borderClickHandler, expandToggleClickHandler, clearAll, selectGradeOpenIndex, setSelectGradeOpenIndex } =
        useNewCarHeader(expanded, toggleExpanded, onShowSelectNewGrade, cars);

    const maxNumberToCompare = useMaxNumberToCompare();
    const expandedLabel = useCompareV2Label("compareBodyTopSelectCar");
    const closedLabel = useCompareV2Label("compareHeaderChooseUpToXCars", {
        maxNumberOfCars: maxNumberToCompare,
    });

    const compareHeaderRef = useRef<HTMLDivElement>(null);

    return (
        <Styles.Wrapper ref={compareHeaderRef} expanded={expanded} scrollbarWidth={scrollbarWidth}>
            <Styles.Actions align={isRTL(settings) ? "right" : "left"}>
                {(cars.length > 1 || expanded) && !compareV2Standalone && (
                    <Button.Primary
                        light
                        center
                        type="button"
                        m={0}
                        aria-labelledby="button-compare-expand-label"
                        aria-expanded={expanded}
                        onClick={expandToggleClickHandler}
                    >
                        <Styles.ButtonText id="button-compare-expand-label">
                            <CompareV2Label label={expanded ? "compareHeaderHide" : "compareHeaderTitle"} />
                        </Styles.ButtonText>
                        <ToggleIconWrapper
                            ml={1}
                            iconColor="primaryBlack"
                            isDown={expanded}
                            top={expanded ? "2px" : 0}
                            aria-hidden="true"
                        >
                            <Icon variant="chevronUp" />
                        </ToggleIconWrapper>
                    </Button.Primary>
                )}
            </Styles.Actions>

            <Styles.ItemWrapper isWide={compareV2Standalone || isLeasing} role="rowgroup">
                <CompareTable
                    items={cars}
                    heading
                    columnChild={(car, index) => {
                        const { title, subtitle } = getCarNameObject(car, settings);
                        return (
                            <CompareItemStyles.Border
                                onClick={() => borderClickHandler(index)}
                                showCursorPointer={expanded}
                                hideBorder={index + 1 === maxNumberToCompare}
                            >
                                <Styles.MaskedPreloader show={car.loading} />
                                <SelectGrade
                                    index={index}
                                    opened={index === selectGradeOpenIndex}
                                    setOpenState={(open: boolean) => setSelectGradeOpenIndex(open ? index : -1)}
                                    title={title}
                                    subtitle={subtitle}
                                    compareHeaderRef={compareHeaderRef}
                                />
                                <CompareItemStyles.IconHolder>
                                    {expanded ? (
                                        <CompareItemStyles.CloseButton
                                            aria-expanded={index === selectGradeOpenIndex}
                                            aria-controls="select-compare-modal-grade-pane"
                                        >
                                            <>
                                                <Icon variant="chevronDown" />
                                                <SrOnly>
                                                    <CompareV2Label label="compareNCA11yButtonModelGradeChange" />
                                                </SrOnly>
                                            </>
                                        </CompareItemStyles.CloseButton>
                                    ) : (
                                        <CompareItemStyles.CloseButton
                                            onClick={(event) => {
                                                if (!expanded) {
                                                    event.stopPropagation();
                                                    dispatch(
                                                        showModal({
                                                            type: MODAL_DELETE_CAR,
                                                            mode: CompareV2Mode.New,
                                                            indexToDelete: index,
                                                        }),
                                                    );
                                                }
                                            }}
                                        >
                                            <Icon variant="close" />
                                            <SrOnly>
                                                <CompareV2Label label="compareNCA11yButtonRemove" />
                                            </SrOnly>
                                        </CompareItemStyles.CloseButton>
                                    )}
                                </CompareItemStyles.IconHolder>
                            </CompareItemStyles.Border>
                        );
                    }}
                    placeholder={() => (
                        <CompareItemStyles.Border
                            showCursorPointer={expanded}
                            onClick={() => borderClickHandler(cars.length)}
                            hideBorder
                        >
                            <SelectGrade
                                index={cars.length}
                                opened={cars.length === selectGradeOpenIndex}
                                setOpenState={(open: boolean) => setSelectGradeOpenIndex(open ? cars.length : -1)}
                                title={expanded ? expandedLabel : closedLabel}
                                compareHeaderRef={compareHeaderRef}
                                placeholder
                            />
                            {expanded && (
                                <CompareItemStyles.IconHolder>
                                    <Icon variant="chevronDown" />
                                </CompareItemStyles.IconHolder>
                            )}
                        </CompareItemStyles.Border>
                    )}
                />
            </Styles.ItemWrapper>

            <Styles.Actions align={isRTL(settings) ? "left" : "right"}>
                <Styles.ButtonClearAll
                    light
                    type="button"
                    aria-labelledby="button-compare-clear-all-cars"
                    onClick={() => clearAll()}
                >
                    <Styles.ButtonText id="button-compare-clear-all-cars">
                        <CompareV2Label label="compareHeaderClearAll" />
                    </Styles.ButtonText>
                    <Icon variant="close" />
                </Styles.ButtonClearAll>
            </Styles.Actions>
        </Styles.Wrapper>
    );
};

export default NewCarHeader;
